import { ChevronDown } from "lucide-react";
import { useState } from "react";
import { useProjectAccounts } from "../lib/accounts";
import { liveItems } from "../lib/domain";
import { useNow } from "../lib/useNow";
import { DueDateTime } from "./DueDateTime";
import { notify } from "../lib/notify";
import { useStore } from "../store";
import type { Task } from "../types";
import { Button } from "./ui/button";

/**
 * Поправить название и описание темы и названия её видео — опечатка, не та
 * тема, переименовали ролик (просьба 07.10.2026). Опубликованные видео не
 * трогаем: по ним уже вышел пост. Права проверяет сервер.
 */
export function EditTopic({ task, onDone }: { task: Task; onDone: () => void }) {
  const editTask = useStore((s) => s.editTask);
  const editItem = useStore((s) => s.editItem);
  // Срок меняет постановщик (09.10.2026, Артур): раньше правились только
  // название и описание. Аккаунт — и монтажёр (25.10.2026): он и выкладывает.
  const scheduler = useStore((s) => s.role) === "client";
  const now = useNow();
  const [due, setDue] = useState(task.due);
  const accounts = useProjectAccounts(task.projectId);
  const items = liveItems(task).filter((i) => i.status !== "published");
  const [title, setTitle] = useState(task.title);
  const [description, setDescription] = useState(task.description ?? "");
  const [names, setNames] = useState<Record<string, string>>(() => Object.fromEntries(items.map((i) => [i.id, i.title])));
  const single = liveItems(task).length === 1 ? items[0] : undefined;
  const [accountId, setAccountId] = useState<string | null>(single?.accountId ?? null);
  // Срок — пока ролик не сдан и не принят: дальше он ничего не значит.
  const dueEditable = scheduler && task.period !== "week" && liveItems(task).some((i) => i.status === "assigned" || i.status === "rework");
  const dueOk = !dueEditable || due === task.due || due > now;
  const ready = dueOk && title.trim().length >= 3 && items.every((i) => (names[i.id] ?? "").trim().length >= 2);
  const field = "w-full rounded-[16px] border border-input bg-background px-4 py-2.5 text-sm shadow-mxs outline-none focus:ring-4 focus:ring-ring/20";

  const save = () => {
    if (!ready) return;
    const dueMoved = dueEditable && due !== task.due;
    if (title.trim() !== task.title || description.trim() !== (task.description ?? "").trim() || dueMoved) {
      editTask(task.id, { title: title.trim(), description: description.trim(), ...(dueMoved ? { due } : {}) });
    }
    if (single && accountId && accountId !== (single.accountId ?? null)) editItem(task.id, single.id, { accountId });
    // Публикация с одним роликом: название у них одно.
    if (single) {
      if (title.trim() !== single.title) editItem(task.id, single.id, { title: title.trim() });
    } else {
      for (const i of items) {
        const next = (names[i.id] ?? "").trim();
        if (next && next !== i.title) editItem(task.id, i.id, { title: next });
      }
    }
    notify("Изменения сохранены", { icon: "check" });
    onDone();
  };

  return (
    <section className="flex flex-col gap-3 rounded-[20px] border border-border bg-muted/30 p-4">
      <label className="flex flex-col gap-1.5">
        <span className="text-[13px] font-semibold">Название</span>
        <input value={title} onChange={(e) => setTitle(e.target.value)} className={field} />
      </label>
      <label className="flex flex-col gap-1.5">
        <span className="text-[13px] font-semibold">Описание</span>
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} className={`${field} resize-none`} />
      </label>
      {dueEditable && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[13px] font-semibold">Дедлайн</span>
          <DueDateTime due={due} now={now} onChange={setDue} />
          {due !== task.due && due <= now && <span className="text-xs text-rework-fg">срок уже прошёл</span>}
        </div>
      )}
      {single && accounts.length > 1 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[13px] font-semibold">Аккаунт</span>
          <label className="relative inline-flex h-8 items-center gap-1.5 rounded-full border bg-background pl-3 pr-2.5 text-[13px] font-medium hover:bg-muted">
            {accounts.find((a) => a.id === (accountId ?? accounts[0]?.id))?.name ?? "—"}
            <ChevronDown className="size-3.5 text-muted-foreground" />
            <select value={accountId ?? ""} onChange={(e) => setAccountId(e.target.value)} className="absolute inset-0 cursor-pointer opacity-0">
              {!accountId && <option value="">не выбран</option>}
              {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </label>
        </div>
      )}
      {items.length > 1 && (
        <div className="flex flex-col gap-1.5">
          <span className="text-[13px] font-semibold">Видео</span>
          {items.map((i) => (
            <input
              key={i.id}
              value={names[i.id] ?? ""}
              onChange={(e) => setNames((prev) => ({ ...prev, [i.id]: e.target.value }))}
              className={field}
            />
          ))}
        </div>
      )}
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onDone}>Отмена</Button>
        <Button onClick={save} disabled={!ready}>Сохранить</Button>
      </div>
    </section>
  );
}
